// client/src/components/bracket/EditableBracketDiagram.js
import React, { useState, useMemo } from 'react'
import ApiPlayoffs from '../../api/ApiPlayoffs'
import toastr from 'toastr'
import './EditableBracketDiagram.css'

/**
 * EditableBracketDiagram
 * Renders an editable playoff bracket where admins can click on player slots
 * to reassign seeds via dropdown selection.
 *
 * Props:
 * - bracket: { seeds: Array, rounds: Array }
 * - leagueId: number
 * - onBracketUpdate: (updatedBracket) => void
 * - onCancel: () => void
 * - height?: number (default 600)
 */
export default function EditableBracketDiagram({
  bracket,
  leagueId,
  onBracketUpdate,
  onCancel,
  height = 600,
}) {
  const [pendingSwaps, setPendingSwaps] = useState({}) // { [playerId]: newSeed }
  const [activeSlot, setActiveSlot] = useState(null) // { matchId, slot: 'home'|'away' }
  const [isSaving, setIsSaving] = useState(false)

  // Build player lookup from seeds
  const players = useMemo(() => {
    if (!bracket?.seeds) return []
    return bracket.seeds
      .filter((s) => s.playerId !== null)
      .map((s) => ({
        id: s.playerId,
        name: s.playerName || `Player ${s.playerId}`,
        originalSeed: s.seed,
        wins: s.wins ?? 0,
        losses: s.losses ?? 0,
      }))
      .sort((a, b) => a.originalSeed - b.originalSeed)
  }, [bracket])

  // Compute effective seeds with pending swaps applied
  const effectiveSeeds = useMemo(() => {
    const seedMap = {}
    players.forEach((p) => {
      seedMap[p.id] = pendingSwaps[p.id] ?? p.originalSeed
    })
    return seedMap
  }, [players, pendingSwaps])

  // Handle player selection from dropdown
  const handlePlayerSelect = (matchId, slot, selectedPlayerId) => {
    if (!activeSlot) return

    // Find the current player in this slot and the selected player
    const match = findMatch(matchId)
    if (!match) return

    // Get the original player ID from the match data
    const currentPlayerId =
      slot === 'home' ? match.home_player_id : match.away_player_id

    // Get current effective seeds (including any pending swaps)
    const currentSeed = getCurrentSeed(currentPlayerId)
    const selectedSeed = getCurrentSeed(selectedPlayerId)

    if (currentPlayerId === selectedPlayerId) {
      setActiveSlot(null)
      return
    }

    // Swap: each player gets the other's current effective seed
    setPendingSwaps((prev) => ({
      ...prev,
      [currentPlayerId]: selectedSeed,
      [selectedPlayerId]: currentSeed,
    }))

    setActiveSlot(null)
  }

  // Find a match by ID
  const findMatch = (matchId) => {
    if (!bracket?.rounds) return null
    for (const round of bracket.rounds) {
      const match = round.matches?.find((m) => m.id === matchId)
      if (match) return match
    }
    return null
  }

  // Get current effective seed for a player
  const getCurrentSeed = (playerId) => {
    return effectiveSeeds[playerId] ?? null
  }

  // Check if there are pending changes
  const hasChanges = Object.keys(pendingSwaps).length > 0

  // Count unique players with changed seeds (more accurate than dividing by 2)
  const pendingSwapCount = useMemo(() => {
    // Count players whose effective seed differs from their original seed
    return players.filter(
      (p) =>
        pendingSwaps[p.id] !== undefined &&
        pendingSwaps[p.id] !== p.originalSeed
    ).length
  }, [pendingSwaps, players])

  // Save changes
  const handleSave = async () => {
    if (!hasChanges) return

    setIsSaving(true)
    try {
      // Convert pendingSwaps to seed_updates format
      const seedUpdates = Object.entries(pendingSwaps).map(
        ([playerId, newSeed]) => ({
          player_id: parseInt(playerId),
          new_seed: newSeed,
        })
      )

      const result = await ApiPlayoffs.updateSeed(leagueId, seedUpdates)
      toastr.success('Bracket seeds updated successfully')
      setPendingSwaps({})
      onBracketUpdate(result.bracket)
    } catch (error) {
      const errorMessage =
        error.response?.data?.error || error.message || 'Failed to update seeds'
      toastr.error(errorMessage)
    } finally {
      setIsSaving(false)
    }
  }

  // Cancel editing
  const handleCancel = () => {
    setPendingSwaps({})
    setActiveSlot(null)
    onCancel()
  }

  // Reset bracket to original standings
  const [isResetting, setIsResetting] = useState(false)
  const [showResetConfirm, setShowResetConfirm] = useState(false)

  const handleReset = async () => {
    setIsResetting(true)
    try {
      // Regenerate the bracket from original standings by calling startPlayoffs again
      // This deletes existing playoff matches and creates new ones from standings
      await ApiPlayoffs.startPlayoffs(leagueId)
      // Then fetch the fresh bracket
      const result = await ApiPlayoffs.getBracket(leagueId)
      toastr.success('Bracket reset to original standings')
      setPendingSwaps({})
      setActiveSlot(null)
      setShowResetConfirm(false)
      onBracketUpdate(result.bracket)
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        'Failed to reset bracket'
      toastr.error(errorMessage)
    } finally {
      setIsResetting(false)
    }
  }

  // Render a player slot (editable)
  const renderPlayerSlot = (match, slot, seed, playerId, playerName) => {
    const isActive =
      activeSlot?.matchId === match.id && activeSlot?.slot === slot
    const player = players.find((p) => p.id === playerId)
    const effectiveSeed = playerId ? getCurrentSeed(playerId) : seed
    const hasSwap = playerId && pendingSwaps[playerId] !== undefined

    // For TBD slots or bye slots, don't make them editable
    if (!playerId) {
      return (
        <div className="player-slot player-slot--tbd">
          <span className="seed-badge">?</span>
          <span className="player-name">TBD</span>
        </div>
      )
    }

    return (
      <div
        className={`player-slot player-slot--editable ${
          isActive ? 'player-slot--active' : ''
        } ${hasSwap ? 'player-slot--changed' : ''}`}
        onClick={(e) => {
          e.stopPropagation()
          setActiveSlot(isActive ? null : { matchId: match.id, slot })
        }}
      >
        <span className="seed-badge">{effectiveSeed}</span>
        <span className="player-name">{playerName}</span>
        {player && (
          <span className="player-record">
            ({player.wins}W-{player.losses}L)
          </span>
        )}
        <span className="edit-indicator">
          <i className="fas fa-exchange-alt"></i>
        </span>

        {isActive && (
          <div className="player-dropdown" onClick={(e) => e.stopPropagation()}>
            <div className="player-dropdown-header">
              Select player to swap with:
            </div>
            <div className="player-dropdown-list">
              {players
                .filter((p) => p.id !== playerId)
                .sort((a, b) => effectiveSeeds[a.id] - effectiveSeeds[b.id])
                .map((p) => (
                  <div
                    key={p.id}
                    className="player-dropdown-item"
                    onClick={() => handlePlayerSelect(match.id, slot, p.id)}
                  >
                    <span className="seed-badge">{effectiveSeeds[p.id]}</span>
                    <span className="player-name">{p.name}</span>
                    <span className="player-record">
                      ({p.wins}W-{p.losses}L)
                    </span>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  // Normalize rounds
  const rounds = useMemo(
    () => (Array.isArray(bracket?.rounds) ? bracket.rounds : []),
    [bracket]
  )

  if (rounds.length === 0) {
    return (
      <div className="text-center py-5 text-muted">
        <i className="fas fa-trophy fa-3x mb-3"></i>
        <p>No bracket data available</p>
      </div>
    )
  }

  return (
    <div className="editable-bracket-container">
      {/* Instructions banner */}
      <div className="alert alert-info mb-3 d-flex align-items-center">
        <i className="fas fa-info-circle me-2"></i>
        <span>
          <strong>Edit Mode:</strong> Click on any player to swap their seed
          with another player. Changes are previewed instantly.
        </span>
      </div>

      {/* Bracket diagram */}
      <div
        className="editable-bracket-diagram"
        style={{ minHeight: height }}
        onClick={() => setActiveSlot(null)}
      >
        {rounds.map((round, roundIndex) => {
          const title =
            round.round === rounds.length
              ? 'Final'
              : round.round === rounds.length - 1
              ? 'Semifinal'
              : round.round === rounds.length - 2
              ? 'Quarterfinal'
              : `Round ${round.round}`

          return (
            <div key={round.round} className="editable-round-column">
              <div className="round-header">{title}</div>
              <div className="round-matches">
                {(round.matches || []).map((match, matchIndex) => {
                  // Extract seed info from columns
                  const homeSeed = match.home_seed ?? null
                  const awaySeed = match.away_seed ?? null

                  const homePlayerName =
                    match.home_first_name && match.home_last_name
                      ? `${match.home_first_name} ${match.home_last_name}`.trim()
                      : match.home_player_id
                      ? `Player ${match.home_player_id}`
                      : 'TBD'

                  const awayPlayerName =
                    match.away_first_name && match.away_last_name
                      ? `${match.away_first_name} ${match.away_last_name}`.trim()
                      : match.away_player_id
                      ? `Player ${match.away_player_id}`
                      : 'TBD'

                  return (
                    <div
                      key={match.id || `${roundIndex}-${matchIndex}`}
                      className="editable-match-card"
                    >
                      <div className="match-header">
                        Match {match.position || matchIndex + 1}
                      </div>
                      {renderPlayerSlot(
                        match,
                        'home',
                        homeSeed,
                        match.home_player_id,
                        homePlayerName
                      )}
                      <div className="match-vs">vs</div>
                      {renderPlayerSlot(
                        match,
                        'away',
                        awaySeed,
                        match.away_player_id,
                        awayPlayerName
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>

      {/* Reset confirmation modal */}
      {showResetConfirm && (
        <div
          className="reset-confirm-overlay"
          onClick={() => setShowResetConfirm(false)}
        >
          <div
            className="reset-confirm-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h5>
              <i className="fas fa-exclamation-triangle text-warning me-2"></i>
              Reset Bracket?
            </h5>
            <p>
              This will reload the bracket from the database, discarding any
              unsaved changes. If you've already saved changes, those will
              remain.
            </p>
            <div className="d-flex gap-2 justify-content-end">
              <button
                className="btn btn-secondary"
                onClick={() => setShowResetConfirm(false)}
                disabled={isResetting}
              >
                Cancel
              </button>
              <button
                className="btn btn-warning"
                onClick={handleReset}
                disabled={isResetting}
              >
                {isResetting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" />
                    Resetting...
                  </>
                ) : (
                  <>
                    <i className="fas fa-undo me-1"></i>
                    Reset
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action bar */}
      <div className="editable-bracket-actions">
        <div className="action-bar-left">
          {hasChanges && (
            <span className="changes-indicator">
              <i className="fas fa-exclamation-circle me-1"></i>
              {pendingSwapCount} player(s) reseeded
            </span>
          )}
        </div>
        <div className="action-bar-right">
          <button
            className="btn btn-outline-warning me-2"
            onClick={() => setShowResetConfirm(true)}
            disabled={isSaving || isResetting}
          >
            <i className="fas fa-undo me-1"></i>
            Reset
          </button>
          <button
            className="btn btn-secondary me-2"
            onClick={handleCancel}
            disabled={isSaving || isResetting}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={!hasChanges || isSaving || isResetting}
          >
            {isSaving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Saving...
              </>
            ) : (
              <>
                <i className="fas fa-save me-2"></i>
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
